/**
 * Firm-scope hook — Case Subtype Taxonomy Section 5.
 *
 * Surfaces the firm's configured `practiceAreas` + `licensedStates` (as seen
 * in FirmInformationPanel) to admin authoring surfaces so workflow
 * definitions and taxonomy admin default to firm scope rather than showing
 * all 50 states + every practice area.
 *
 * Platform admins can opt out via `overrideAll`, restoring the full scope.
 */

import { useEffect, useState } from 'react';

export interface FirmScope {
  practiceAreas: string[];       // canonical storage keys (snake_case)
  practiceAreasDisplay: string[]; // original display labels as entered
  licensedStates: string[];       // uppercase two-letter codes
  loading: boolean;
  error: string | null;
  /** Platform admins can override to view/author everywhere. */
  overrideAll: boolean;
  setOverrideAll: (value: boolean) => void;
}

function normalizePracticeAreaKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function normalizeStateCode(value: string): string | null {
  const v = value.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(v) ? v : null;
}

export function useFirmScope(): FirmScope {
  const [practiceAreas, setPracticeAreas] = useState<string[]>([]);
  const [practiceAreasDisplay, setPracticeAreasDisplay] = useState<string[]>([]);
  const [licensedStates, setLicensedStates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overrideAll, setOverrideAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('webelieve_access_token') || '';
        const response = await fetch('/api/v1/admin/firm-settings', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          if (!cancelled) {
            setPracticeAreas([]);
            setPracticeAreasDisplay([]);
            setLicensedStates([]);
            setError(`Firm settings unavailable (HTTP ${response.status})`);
          }
          return;
        }
        const firm = await response.json();
        const rawPAs: string[] = Array.isArray(firm?.practiceAreas) ? firm.practiceAreas : [];
        const rawStates: string[] = Array.isArray(firm?.licensedStates) ? firm.licensedStates : [];
        if (cancelled) return;
        setPracticeAreasDisplay(rawPAs.map((v) => String(v).trim()).filter(Boolean));
        setPracticeAreas(rawPAs.map((v) => normalizePracticeAreaKey(String(v))).filter(Boolean));
        setLicensedStates(
          rawStates
            .map((v) => normalizeStateCode(String(v)))
            .filter((v): v is string => !!v),
        );
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unknown firm-scope error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return {
    practiceAreas,
    practiceAreasDisplay,
    licensedStates,
    loading,
    error,
    overrideAll,
    setOverrideAll,
  };
}

/**
 * Helper for non-React callers (tests, utilities) that just need the
 * normalization.
 */
export const firmScopeNormalizers = {
  practiceAreaKey: normalizePracticeAreaKey,
  stateCode: normalizeStateCode,
};
